import { useNavigate } from "react-router-dom";
// lucid-react
import {
  Building2,
  BookOpen as IconBookOpen,
  Settings2 as IconSettings,
  UserRound as IconUserRound,
  Users as IconUsers,
  LucideIcon,
} from "lucide-react";
// @mui
import { Box, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
// hooks
import useLocales from "../../../hooks/useLocales";
// routes
import { PATH_AUTH, PATH_DASHBOARD } from "../../../routes/paths";
// @mui
import { MenuItem, PopoverActions } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import { ReactElement, useEffect, useRef, useState } from "react";
import { Permissions } from "src/@types/permissions";
import ChevronDown from "src/assets/icon/chevron/ChevronDown";
import ChevronUpIcon from "src/assets/icon/chevron/up";
import { GetAsync } from "src/common/httpRequests";
import { resetLocalStorage } from "src/common/resetLocalStorage";
import Label from "src/components/Label";
import MenuPopover from "src/components/MenuPopover";
import PermissionInfo from "src/components/permissions/permissionInfo";
import {
  API_GATEWAY,
  ENABLE_USER_ACCOUNT_STATUS,
} from "src/constants/settings";
import useEnabledModules from "src/hooks/useEnabledModules";
import usePermissions from "src/hooks/usePermissions";
import { resetMixpanel } from "src/mixpanel/Mixpanel";
import { MixpanelFunction } from "src/mixpanel/mixpanelFunction";
import { store, useSelector } from "src/redux/store";
import MyAvatar from "../../../components/MyAvatar";
import useAuth from "../../../hooks/useAuth";
import AccountLogoutMenu from "./AccountLogoutMenu";
import AccountSwitcher, { useAccountNotice } from "./AccountSwitcher";
import OverflowTooltip from "./OverFlowToolTip";
// ----------------------------------------------------------------------

const RootStyle = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  border: `1px solid ${theme.palette.text.borderLine}`,
  padding: theme.spacing(1, 1.5),
  borderRadius: Number(theme.shape.borderRadius) * 1,
  backgroundColor: theme.palette.background.paper,
  transition: theme.transitions.create("opacity", {
    duration: theme.transitions.duration.shorter,
  }),
}));

// ----------------------------------------------------------------------

type MenuItemConfig = {
  icon: LucideIcon;
  title: string;
  to?: string;
  action?: () => void;
  locked?: boolean;
  moduleLocked?: boolean;
};

type TAccountSection = "switch" | "logout";

type Props = {
  isCollapse: boolean | undefined;
};

export default function NavbarAccount({ isCollapse }: Props) {
  const userInfo = useSelector((state: any) => state.credential.userCred);
  const {
    org: { plan },
  } = store.getState()?.organization;
  const [accountStatus, setAccountStatus] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  useAccountNotice();
  const popoverActions = useRef<PopoverActions>(null);
  const [menuPaper, setMenuPaper] = useState<HTMLDivElement | null>(null);
  const [openSection, setOpenSection] = useState<TAccountSection | null>(null);

  useEffect(() => {
    if (!menuPaper) return;
    const observer = new ResizeObserver(() =>
      popoverActions.current?.updatePosition(),
    );
    observer.observe(menuPaper);
    return () => observer.disconnect();
  }, [menuPaper]);

  const toggleSection = (section: TAccountSection) =>
    setOpenSection((open) => (open === section ? null : section));

  const onClose = () => {
    setAnchorEl(null);
    setOpenSection(null);
  };

  const getAccountStatus = () => {
    GetAsync(`${API_GATEWAY}/api/userservice/account/status`)
      .then((res) => {
        setAccountStatus(res.data);
      })
      .catch((err) => {});
  };

  const theme = useTheme();

  const COMPANY_ID: string | null = localStorage.getItem("company_id");

  function redirectToAcademy(): void {
    window.open("https://academy.mydost.ai/knowledge");
    MixpanelFunction({
      eventName: "Clicked Dost Academy",
      organizationId: COMPANY_ID,
    });
  }

  const handleLogout = async () => {
    try {
      await logout();
      // shutDownIntercomm();
      resetMixpanel();
      navigate(PATH_AUTH.login, { replace: true });
      resetLocalStorage();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Unable to logout!", { variant: "error" });
    }
  };

  useEffect(() => {
    if (ENABLE_USER_ACCOUNT_STATUS) {
      getAccountStatus();
    }
  }, [userInfo]);
  const { moduleHandle } = useEnabledModules();
  const userModule = moduleHandle(["UserRole"]);
  const { permissionHandle } = usePermissions();
  const readDeveloperPage = permissionHandle([
    "DeveloperSettings_Read",
    "DeveloperSettings_Write",
  ]);
  const readUser = permissionHandle([
    "UserManagement_Read",
    "UserManagement_Write",
    "RoleManagement_Read",
    "RoleManagement_Write",
  ]);

  const readCompany = permissionHandle([Permissions.Company_Read]);

  const menuItems: MenuItemConfig[] = [
    { icon: IconBookOpen, title: "Dost Academy", action: redirectToAcademy },
    { icon: IconUserRound, title: "My account", to: "/dashboard/user/account" },
    {
      icon: IconUsers,
      title: "Users management",
      to: "/dashboard/usersV2",
      locked: !readUser,
      moduleLocked: !userModule,
    },
    {
      icon: Building2,
      title: "companies",
      to: PATH_DASHBOARD.general.companies,
      locked: !readCompany,
      moduleLocked: !userModule,
    },
    {
      icon: IconSettings,
      title: "Developers settings",
      to: "/dashboard/developers-settings",
      locked: !readDeveloperPage,
    },
  ];

  return (
    <>
      <RootStyle sx={{ ...(isCollapse && { px: 1, py: 0.25 }) }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1.5,
            width: "100%",
            cursor: "pointer",
            alignItems: "center",
            ...(isCollapse && { justifyContent: "center" }),
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setAnchorEl(e.currentTarget);
          }}
        >
          <Box display="flex" gap={isCollapse ? 0 : 1.5} alignItems="center">
            <MyAvatar user={userInfo} />
            <Box
              sx={{
                transition: (theme) =>
                  theme.transitions.create("width", {
                    duration: theme.transitions.duration.shorter,
                  }),
                ...(isCollapse && { ml: 0, width: 0, overflow: "hidden" }),
              }}
            >
              <OverflowTooltip variant="subtitle2">
                {userInfo?.firstName ?? ""}
              </OverflowTooltip>
              <OverflowTooltip
                variant="caption"
                sx={{ color: "text.label", fontWeight: 400 }}
              >
                {userInfo?.email ?? ""}
              </OverflowTooltip>
              {accountStatus ? (
                <Tooltip
                  title={
                    !accountStatus?.isEmailVerified
                      ? translate("navbar_account.Unverified")
                      : ""
                  }
                >
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      color: "text.secondary",
                      fontSize: theme.spacing(1.2),
                    }}
                  >
                    {!accountStatus?.isEmailVerified && (
                      <Label
                        variant={
                          theme.palette.mode === "light" ? "ghost" : "filled"
                        }
                        color="error"
                        sx={{
                          textTransform: "capitalize",
                          height: "0.9rem",
                          fontWeight: 500,
                        }}
                      >
                        {translate("navbar_account.Unverified")}
                      </Label>
                    )}
                  </Typography>
                </Tooltip>
              ) : (
                <>
                  <Skeleton variant="text" width={80} height={15} />
                </>
              )}
            </Box>
          </Box>
          {!isCollapse && (
            <Box
              id="composition-button"
              sx={{
                position: "relative",
                my: "auto",
                transition: (theme) =>
                  theme.transitions.create("width", {
                    duration: theme.transitions.duration.shorter,
                  }),
              }}
            >
              {anchorEl === null ? (
                <ChevronDown height={20} width={20} />
              ) : (
                <ChevronUpIcon height={20} width={20} />
              )}
            </Box>
          )}
        </Box>
        <MenuPopover
          disabledArrow
          action={popoverActions}
          PaperProps={{ ref: setMenuPaper }}
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={onClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: 0, horizontal: 0 }}
          arrow="right-top"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            mt: -1,
            width: 250,
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            "& .MuiMenuItem-root": {
              px: 1,
              typography: "body2",
              borderRadius: 0.75,
            },
            backgroundColor: theme.palette.common.white,
            borderRadius: "12px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              p: 1,
              borderBottom: `0.5px solid ${theme.palette.divider}`,
            }}
          >
            <Label
              variant="outlined"
              color="primary"
              sx={{
                textTransform: "capitalize",
                fontWeight: 500,
                fontSize: theme.spacing(1.75),
                my: "auto",
              }}
            >
              {translate(plan)}
            </Label>
          </Box>
          <AccountSwitcher
            onClose={onClose}
            isListOpen={openSection === "switch"}
            onToggleList={() => toggleSection("switch")}
          />
          <Box>
            {menuItems.map((item) => (
              <NavBarAccountMenuItem
                key={item.title}
                onClick={() => {
                  if (item.to) navigate(item.to);
                  else item.action?.();
                  onClose();
                }}
                icon={item.icon}
                title={translate(item.title)}
                locked={item.locked}
                moduleLocked={item.moduleLocked}
              />
            ))}
            <AccountLogoutMenu
              onLogoutAll={handleLogout}
              isOpen={openSection === "logout"}
              onToggle={() => toggleSection("logout")}
            />
          </Box>
        </MenuPopover>
      </RootStyle>
    </>
  );
}

// ----------------------------------------------------------------------

type NavBarAccountMenuItemProps = {
  onClick?: any;
  icon: LucideIcon;
  endIcon?: ReactElement;
  title: string;
  locked?: boolean;
  moduleLocked?: boolean;
};

const NavBarAccountMenuItem = ({
  onClick,
  icon: Icon,
  endIcon,
  locked,
  moduleLocked,
  title,
}: NavBarAccountMenuItemProps) => {
  const theme = useTheme();

  const handleClick = (e) => {
    if (locked) {
      return;
    }
    onClick(e);
  };

  return (
    <MenuItem onClick={handleClick}>
      <Box sx={{ "& svg": { mr: 2, width: 20, height: 20 } }}>
        <Icon color={theme.palette.text.label} strokeWidth={1.5} />
      </Box>
      <Typography
        variant="body2"
        color="text.label"
        sx={{ textTransform: "capitalize" }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          ml: "auto",
        }}
      >
        {locked ? <PermissionInfo moduleLocked={moduleLocked} /> : endIcon}
      </Box>
    </MenuItem>
  );
};